import { FC, useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { uuid } from '../../../../_metronic/helpers';
import { Client, EventLog } from '../../../helpers/models';
import { getEventLogFromEvent, useEvents, useEventsLog } from '../../../helpers/api';
import { dateUTC } from '../../apps/schedule/core/helpers';
import { fixToUTC, getRRwithExdates } from '../../../helpers/funcs';

export type SessionChoice = {
  key: string;
  start: Date;
  label: string;
  serviceName?: string;
  eventLogUuid?: uuid;
  eventUuid?: uuid;
};

type Props = {
  client: Client;
  show: boolean;
  onCancel: () => void;
  onSubmit: (choice: SessionChoice) => void;
  /** When set, the session list centers on this date ±14 days (used for re-attaching). */
  referenceDate?: Date;
};

const DAYS_BACK = 30;
const DAYS_AHEAD = 5;
const RELINK_WINDOW_DAYS = 14;

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const serviceNameOf = (evl: EventLog): string | undefined => {
  const ps: any = (evl as any).provider_service;
  return ps?.service?.name || ps?.parent?.name || ps?.name;
};

const LinkToSessionModal: FC<Props> = (props) => {
  const { client, show, onCancel, onSubmit, referenceDate } = props;
  const [selectedKey, setSelectedKey] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const { start, end } = useMemo(() => {
    if (referenceDate) {
      const s = moment(referenceDate).startOf('day').subtract(RELINK_WINDOW_DAYS, 'days').toDate();
      const e = moment(referenceDate).endOf('day').add(RELINK_WINDOW_DAYS, 'days').toDate();
      return { start: s, end: e };
    }
    const s = moment().startOf('day').subtract(DAYS_BACK, 'days').toDate();
    const e = moment().endOf('day').add(DAYS_AHEAD, 'days').toDate();
    return { start: s, end: e };
  }, [referenceDate]);

  const { data: events, status: eventsStatus } = useEvents({ start, end, client, enabled: show });
  const { data: eventLogs, status: logsStatus } = useEventsLog({ start, end, client, enabled: show });

  const choices: SessionChoice[] = useMemo(() => {
    const list: SessionChoice[] = [];
    const loggedByKey = new Map<string, EventLog>();
    eventLogs?.forEach((evl) => {
      if (!evl.start) return;
      const startDate = new Date(evl.start);
      const key = `${evl.event?.uuid || evl.uuid}_${startDate.toISOString()}`;
      loggedByKey.set(key, evl);
    });

    events?.forEach((ev: any) => {
      if (!ev?.rrule) return;
      try {
        const rr = getRRwithExdates(ev);
        const dates = rr.between(dateUTC(start), dateUTC(end)).map((d: Date) => fixToUTC(d));
        dates.forEach((d) => {
          const key = `${ev.uuid}_${d.toISOString()}`;
          const existing = loggedByKey.get(key);
          const startDate = existing?.start ? new Date(existing.start) : d;
          const serviceName =
            (existing && serviceNameOf(existing)) ||
            ev.provider_service?.service?.name ||
            ev.provider_service?.parent?.name ||
            ev.provider_service?.name;
          list.push({
            key,
            start: startDate,
            label: moment(startDate).format('LLL'),
            serviceName,
            eventLogUuid: existing?.uuid,
            eventUuid: ev.uuid,
          });
          loggedByKey.delete(key);
        });
      } catch {
        // ignore malformed rrule
      }
    });

    // Any leftover event logs that didn't match an event rrule expansion
    loggedByKey.forEach((evl, key) => {
      if (!evl.start) return;
      const startDate = new Date(evl.start);
      list.push({
        key,
        start: startDate,
        label: moment(startDate).format('LLL'),
        serviceName: serviceNameOf(evl),
        eventLogUuid: evl.uuid,
        eventUuid: evl.event?.uuid,
      });
    });

    return list.sort((a, b) => b.start.getTime() - a.start.getTime());
  }, [events, eventLogs, start, end]);

  useEffect(() => {
    if (!show || selectedKey) return;
    if (!choices.length) return;
    if (referenceDate) {
      const refMatch = choices.find((c) => sameDay(c.start, referenceDate));
      if (refMatch) {
        setSelectedKey(refMatch.key);
        return;
      }
    }
    const today = new Date();
    const yesterday = moment().subtract(1, 'day').toDate();
    const todayMatch = choices.find((c) => sameDay(c.start, today));
    if (todayMatch) {
      setSelectedKey(todayMatch.key);
      return;
    }
    const yesterdayMatch = choices.find((c) => sameDay(c.start, yesterday));
    if (yesterdayMatch) setSelectedKey(yesterdayMatch.key);
  }, [show, choices, selectedKey, referenceDate]);

  const loading = eventsStatus === 'loading' || logsStatus === 'loading';

  const handleSubmit = async () => {
    const choice = choices.find((c) => c.key === selectedKey);
    if (!choice) return;
    setSubmitting(true);
    try {
      let eventLogUuid = choice.eventLogUuid;
      if (!eventLogUuid && choice.eventUuid) {
        const fresh = await getEventLogFromEvent({ event_uuid: choice.eventUuid, datetime: choice.start });
        eventLogUuid = fresh?.uuid;
      }
      if (!eventLogUuid) {
        setSubmitting(false);
        return;
      }
      onSubmit({ ...choice, eventLogUuid });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Attach Note to Session</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" /> Loading sessions…
          </div>
        ) : choices.length === 0 ? (
          <div className="text-muted text-center py-4">
            {referenceDate
              ? `No sessions found within ${RELINK_WINDOW_DAYS} days of the currently attached session.`
              : 'No sessions found in the last 30 days or next 5 days.'}
          </div>
        ) : (
          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            {choices.map((c) => (
              <label
                key={c.key}
                className="d-flex align-items-center py-2 px-3 border-bottom"
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  className="form-check-input me-3"
                  name="link-session-choice"
                  value={c.key}
                  checked={selectedKey === c.key}
                  onChange={() => setSelectedKey(c.key)}
                />
                <div className="flex-grow-1">
                  <div className="fw-bold">{c.label}</div>
                  {c.serviceName && <div className="text-muted fs-7">{c.serviceName}</div>}
                </div>
                {!c.eventLogUuid && <span className="badge badge-light-info ms-2">Scheduled</span>}
              </label>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-outline btn-outline-primary btn-sm me-2"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSubmit}
          disabled={!selectedKey || submitting}
        >
          Attach {submitting && <Spinner className="ms-2" animation="border" size="sm" />}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export { LinkToSessionModal };
